import React, { useEffect, useRef, useState } from 'react';
import { useBlocklyWorkspace } from 'react-blockly';
import Blockly from 'blockly';
import { Link, Prompt, useParams } from 'react-router-dom';
import {
    Breadcrumb,
    Container,
    Divider,
    Segment,
} from 'semantic-ui-react';
import axios from 'axios';
import { Validator } from 'jsonschema';

import { WorkspaceConfig } from '../components/editor/workspace-config';

import '../components/editor/generator';

import Schema from '../components/editor/schema';
import Menu from '../components/editor/menu';
import Params from '../components/editor/parammanager';

import { useAuth, useError } from '../providers';
import useSupportModal from '../hooks/useSupportModal';

const Editor = () => {
    const [code, setCode] = useState({});
    const [xml, setXml] = useState({});
    const [codeView, setCodeView] = useState(false);
    const [selected, setSelected] = useState(null);
    const [isChanged, setIsChanged] = useState(false);

    const blocklyRef = useRef(null);
    const validator = new Validator();

    const { showError } = useError();
    const { showSupportModal } = useSupportModal();
    const { user } = useAuth();

    window.ShowEditorHelp = (el, title, content) => {
        el.setHelpUrl(true);
        return () => {
            showSupportModal({
                title: title,
                message: content
            });
        }
    }

    const { command } = useParams();

    const initialXml = '<xml xmlns="http://www.w3.org/1999/xhtml"><block type="command_definition" x="30" y="30" /></xml>';

    const { workspace } = useBlocklyWorkspace({
        ref: blocklyRef,
        initialXml: initialXml,
        workspaceConfiguration: WorkspaceConfig,
    });

    const addBlock = (e, { value }) => {
        let parent = workspace.getBlocksByType('command_definition')[0];
        var container = document.querySelector('[aria-label="Blockly Workspace"]');
        let block = workspace.newBlock(value);
        block.initSvg();
        block.render();
        block.moveBy((container.clientWidth / 2) - (block.width / 2), (container.clientHeight / 2) - (block.height / 2));

        if (selected) {
            try {
                // Try to connect the new block to the selected item...
                let selBlock = workspace.getBlockById(selected);
                const parentConnection = selBlock.getInput('Input').connection;
                const childConnection = block.outputConnection;
                parentConnection.connect(childConnection);
            } catch (e) { }
        }

        try {
            const [lastChild] = parent.getChildren().slice(-1);
            if (lastChild) {
                // Try to connect a new block to the bottom of the stack.
                lastChild.lastConnectionInStack().sourceBlock_.nextConnection.connect(block.previousConnection);
            } else {
                // If the stack is empty, add it as the first element in the parent container.
                parent.getInput('Content').connection.connect(block.previousConnection);
            }
        } catch (e) { }

        block.select()
    }

    const toggleCodeView = (e, { name }) => {
        if (codeView) {
            setCodeView(false);
        } else {
            const dom = Blockly.Xml.workspaceToDom(workspace);
            setXml(Blockly.Xml.domToPrettyText(dom));
            setCode(Blockly.JSON.workspaceToCode(workspace));

            // setErrors(validator.validate(Blockly.JSON.workspaceToCode(workspace), Schema.build()).errors);

            if (Blockly.selected) {
                Blockly.selected.unselect();
            }
            setCodeView(true);
        }
    }

    const saveCustomCommand = async (e) => {
        const dom = Blockly.Xml.workspaceToDom(workspace);
        const xml = Blockly.Xml.domToPrettyText(dom);
        const code = Blockly.JSON.workspaceToCode(workspace);

        const validation = validator.validate(Blockly.JSON.workspaceToCode(workspace), Schema.build())
        if (validation.errors.length) {
            showError(<center>
                <p>Validation errors:</p>
                {Object.keys(validation.errors).map(key =>
                    <p>{validation.errors[key].message}</p>
                )}
            </center>);
            return;
        }

        try {
            const { data } = await axios.post(
                '/api/custom/save',
                {
                    code,
                    command,
                    xml
                },
            );

            switch (data.status) {
                case 200: // Save was successful.
                    setIsChanged(false);
                    window.location.assign("/dashboard/custom");
                    break;
                case 500: // Internal server
                default:  // Anything else
                    showError(<center>An unknown error occured while trying to save your changes.</center>);
                    break;
            }
        } catch (err) {
            showError(<center>An unknown error occured while trying to save your changes.</center>);
        }
    }

    const updateSelected = (e) => {
        if (e.type === 'selected') {
            setSelected(e.newElementId);
        } else if (e.type === 'finised_loading') {
            setIsChanged(false);
        } else {
            if (e.type === 'change' && e.name === 'Syntax') {
                setIsChanged(false);
            } else {
                setIsChanged(true);
            }
        }
    }

    useEffect(() => {
        if (workspace && command) {
            async function getCustomCommand(commmand) {
                try {
                    const result = await axios(
                        `/api/custom/${command}`,
                    );
                    if (result.status === 200) {
                        workspace.clear();
                        Blockly.Xml.domToWorkspace(Blockly.Xml.textToDom(result.data), workspace);
                        Blockly.mainWorkspace.trashcan.emptyContents()
                        setIsChanged(false);
                    } else {
                        console.log(result)
                        showError(<center>No such command.</center>);
                    }
                } catch (err) {
                    console.log(err)
                    showError(<center>No such command.</center>);
                }
            }
            getCustomCommand(command);
        }
    }, [command, workspace, showError]);

    useEffect(() => {
        if (workspace) {
            workspace.addChangeListener(Blockly.Events.disableOrphans);
            workspace.addChangeListener(updateSelected)
        }
    }, [workspace]);

    useEffect(() => {
        Params.reset()
    }, [code])

    return (
        <Segment className='lobby' inverted>
            <Prompt
                when={isChanged}
                message="You haven't saved your changes; are you sure you want to leave?" />
            <Container text className='description'>
                <Breadcrumb size='huge'>
                    <Breadcrumb.Section link as={Link} to='/dashboard'>Dashboard</Breadcrumb.Section>
                    <Breadcrumb.Divider icon='right chevron' />
                    <Breadcrumb.Section link as={Link} to='/dashboard/custom'>Custom Commands</Breadcrumb.Section>
                    <Breadcrumb.Divider icon='right chevron' />
                    <Breadcrumb.Section active>{command ? 'Edit Command' : 'Create Command'}</Breadcrumb.Section>
                </Breadcrumb>
                <Divider hidden />
                <p>
                    {command ?
                        'Edit your command:' :
                        'Create a custom command.'
                    }
                </p>
            </Container>
            <Menu addBlock={addBlock} toggleCodeView={toggleCodeView} save={saveCustomCommand} codeView={user.username === 'sublimnl'} />
            <Segment className='lobby' inverted attached='bottom' style={{ backgroundColor: '#333', padding: '0em 0em' }}>
                <div ref={blocklyRef} className='fill-height' style={{ display: codeView ? 'none' : 'revert' }} />
                <textarea
                    id="code"
                    className='fill-height'
                    style={{
                        width: '100%',
                        backgroundColor: 'transparent',
                        color: 'white',
                        display: codeView ? 'revert' : 'none'
                    }}
                    value={JSON.stringify(code, null, 2) + "\n\n" + xml + "\n\n" + JSON.stringify(Schema.build(), null, 2)}
                    readOnly
                ></textarea>
            </Segment>
        </Segment>
    )
};

export default Editor;
